import type { ReactNode } from 'react';
import type { DerivedPolicy } from '@yourstore/domain';
import type { BrandingSection, ZoneReport } from '../../../wire';
import { useT } from '../../../messages';
import type { BlockSpec } from './specs';
import { BAND_SLOT_NAMES, DATA_SOURCES, sourceHref, viewOf, viewsOf } from './specs';
import { SlotField, optionLabel } from './slots';
import { Thumb } from './thumbs';
import { sectionName, sectionPurpose, viewLabel } from './names';

/**
 * **The inspector replaces the form editor** (the mockup's note): the
 * selected section's name and purpose, how it looks (its views, as
 * pictures), its background, what it says (its slots as controls), and —
 * for a section that shows the business's data — where that data lives, with
 * a link there instead of a copy of it.
 *
 * Every control writes one slot of one section in the one document; the
 * builder turns that into an undo step and a draft for the canvas.
 */
export function Inspector({
  tenantId,
  section,
  index,
  count,
  spec,
  pinned,
  pageKind,
  hidden,
  zones,
  zonePrefix,
  policyRules,
  onSlot,
  onMove,
  onDuplicate,
  onRemove,
}: {
  tenantId: string;
  section: BrandingSection | null;
  index: number;
  count: number;
  spec: BlockSpec | undefined;
  pinned: boolean;
  pageKind: string;
  /** The canvas drew nothing for it — it has nothing to show yet. */
  hidden: boolean;
  zones: readonly ZoneReport[];
  /** `sections` or `pages.<slug>.sections` — how the sanitizer's report names this page. */
  zonePrefix: string;
  policyRules: readonly DerivedPolicy[];
  onSlot: (slot: string, value: unknown) => void;
  onMove: (delta: number) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}): ReactNode {
  const t = useT();

  if (section === null) {
    return (
      <div className="bld-inspector__empty">
        <p>{t('builder.inspector.none')}</p>
      </div>
    );
  }

  const name = sectionName(t, section.type, spec);
  const views = viewsOf(spec);
  const current = viewOf(spec, section.slots);
  const slots = spec?.slots ?? [];
  const tone = slots.find((slot) => slot.name === 'tone');
  const band = slots.filter(
    (slot) => BAND_SLOT_NAMES.includes(slot.name) && slot.name !== 'tone' && slot.name !== 'kicker',
  );
  const words = slots.filter(
    (slot) =>
      slot.name !== 'variant' && (!BAND_SLOT_NAMES.includes(slot.name) || slot.name === 'kicker'),
  );
  const source = DATA_SOURCES[section.type];
  const offPage = spec !== undefined && !spec.pages.includes(pageKind);
  const repeatable = spec?.repeatable === true;

  const field = (slot: (typeof slots)[number]): ReactNode => (
    <SlotField
      key={slot.name}
      slot={slot}
      value={section.slots[slot.name]}
      onChange={(value) => onSlot(slot.name, value)}
      zone={zones.find((z) => z.path === `${zonePrefix}[${String(index)}].slots.${slot.name}`)}
      policyRules={section.type === 'policies' && slot.kind === 'rows' ? policyRules : undefined}
    />
  );

  return (
    <div className="bld-inspector__body">
      <div className="bld-ins-head">
        <h2>{name}</h2>
        <p>{sectionPurpose(t, section.type, spec)}</p>
        {pinned ? <p className="bld-hint">{t('builder.inspector.pinned')}</p> : null}
      </div>

      {offPage ? (
        <p className="bld-warn" role="note">
          {t('builder.inspector.offPage', { name })}
        </p>
      ) : null}
      {hidden && !offPage ? (
        <p className="bld-hint" role="note">
          {source === undefined
            ? t('builder.inspector.hiddenWords')
            : t('builder.inspector.hiddenData')}
        </p>
      ) : null}

      <div className="bld-ins-tools" role="group" aria-label={t('builder.inspector.arrange')}>
        <button
          type="button"
          className="button button--sm"
          disabled={index === 0}
          onClick={() => onMove(-1)}
        >
          {t('builder.action.up')}
        </button>
        <button
          type="button"
          className="button button--sm"
          disabled={index >= count - 1}
          onClick={() => onMove(1)}
        >
          {t('builder.action.down')}
        </button>
        {repeatable || !pinned ? (
          <button
            type="button"
            className="button button--sm"
            disabled={!repeatable}
            title={repeatable ? undefined : t('builder.inspector.onlyOne')}
            onClick={onDuplicate}
          >
            {t('builder.action.duplicate')}
          </button>
        ) : null}
        {pinned ? null : (
          <button type="button" className="button button--sm button--quiet" onClick={onRemove}>
            {t('builder.action.remove')}
          </button>
        )}
      </div>

      {views.length > 1 ? (
        <section className="bld-ins-group" aria-labelledby="bld-views">
          <h3 id="bld-views" className="bld-label">
            {t('builder.inspector.looks')}
          </h3>
          <div className="bld-views" role="group" aria-labelledby="bld-views">
            {views.map((view) => (
              <button
                key={view}
                type="button"
                className="bld-view"
                aria-pressed={view === current}
                onClick={() => onSlot('variant', view === views[0] ? undefined : view)}
              >
                <Thumb type={section.type} view={view} />
                <span>{viewLabel(t, view)}</span>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {tone === undefined && band.length === 0 ? null : (
        <section className="bld-ins-group" aria-labelledby="bld-ground">
          <h3 id="bld-ground" className="bld-label">
            {t('builder.inspector.background')}
          </h3>
          {tone === undefined ? null : (
            <div className="bld-choice" role="group" aria-labelledby="bld-ground">
              {(tone.options ?? []).map((option) => {
                const chosen =
                  (typeof section.slots['tone'] === 'string' && section.slots['tone'] !== ''
                    ? section.slots['tone']
                    : tone.fallback) === option;
                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={chosen}
                    onClick={() => onSlot('tone', option === tone.fallback ? undefined : option)}
                  >
                    <i className={`bld-swatch-dot bld-swatch-dot--${option}`} aria-hidden="true" />
                    {t('builder.tone', { tone: option }) || optionLabel(t, option)}
                  </button>
                );
              })}
            </div>
          )}
          {band.length === 0 ? null : (
            <details className="bld-more">
              <summary>{t('builder.inspector.bandMore')}</summary>
              <div className="bld-stack">{band.map(field)}</div>
            </details>
          )}
        </section>
      )}

      <section className="bld-ins-group" aria-labelledby="bld-words">
        <h3 id="bld-words" className="bld-label">
          {t('builder.inspector.says')}
        </h3>
        {source === undefined ? null : (
          <div className="bld-bind">
            <strong>{t('builder.bind.title', { source: t('builder.source', { source }) })}</strong>
            <span className="bld-hint">{t('builder.bind.hint', { source })}</span>
            <a className="linkish" href={sourceHref(source, tenantId)}>
              {t('builder.bind.go', { source })}
            </a>
          </div>
        )}
        {section.type === 'gallery' ? (
          <p className="bld-hint">
            {t('builder.inspector.photos')}{' '}
            <a className="linkish" href={sourceHref('photos', tenantId)}>
              {t('builder.inspector.photosGo')}
            </a>
          </p>
        ) : null}
        {words.length === 0 ? (
          <p className="bld-hint">{t('builder.inspector.nothingToSay')}</p>
        ) : (
          <div className="bld-stack">{words.map(field)}</div>
        )}
      </section>
    </div>
  );
}
